import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ChevronRight, Truck, Clock3, BarChart3, GraduationCap, Snowflake, Package, ShieldCheck, Leaf, Handshake } from 'lucide-react';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import Wholesale from '@/components/sections/Wholesale';
import { Button } from '@/components/ui/button';

const CAMPUS_IMG = 'https://customer-assets.emergentagent.com/job_vegan-danish-bakery/artifacts/xlvegc3y_Background2.png';

const VALUE_PROPS = [
  { Icon: Snowflake, title: 'Frozen-to-oven ready', body: 'Bake from -23°C in 18–22 minutes at 165°C convection. No proofing, no scratch labor.' },
  { Icon: Truck, title: 'Nationwide LTL delivery', body: 'Weekly refrigerated dock handoff to every province. Pallet program starts at 108 cases mix-and-match.' },
  { Icon: Package, title: 'Case-ready SKUs', body: 'Individually film-wrapped or kraft-sleeved. Merchandising cards and bake-off guides included.' },
  { Icon: BarChart3, title: 'Velocity reporting', body: 'POS-integrated dashboards. Restock by SKU. Blended margin tracking on request.' },
  { Icon: ShieldCheck, title: 'Spec-driven quality', body: 'Repeatable, audited macros and allergen sheets for K-12, university and corporate dining.' },
  { Icon: GraduationCap, title: 'Campus-tested', body: 'Recipes piloted on 8 campuses before launch. Student-approved, operator-approved.' }
];

const PROGRAM = [
  { l: 'Minimum order', v: '1 pallet · 108 cases (mix-and-match)' },
  { l: 'SKUs available', v: '8 product lines · 40+ individual SKUs' },
  { l: 'Delivery lead time', v: '7–10 business days' },
  { l: 'Direct-store delivery', v: 'Available within 300 km of Toronto' },
  { l: 'Bake yield', v: '18-min bake · 9 sec labor per pastry' },
  { l: 'Case shelf life', v: '180–270 days frozen · 24–48 hrs ambient' }
];

const ORG_TYPES = [
  'University & College Dining',
  'K-12 School Districts',
  'Hotels & Hospitality',
  'Corporate Dining',
  'YMCA & Community Centres',
  'Independent Cafés',
  'Micro-Markets',
  'Airlines & Rail'
];

export default function WholesalePage() {
  return (
    <main className="min-h-screen bg-background">
      <Header variant="solid" />

      {/* HERO */}
      <section className="relative pt-28 pb-16 md:pt-36 md:pb-24 overflow-hidden">
        <div className="absolute inset-0 bg-grain opacity-60 pointer-events-none" />
        <div className="absolute -top-32 -left-32 h-[28rem] w-[28rem] rounded-full bg-leaf-soft/40 blur-3xl pointer-events-none" />
        <div className="absolute top-20 -right-32 h-[26rem] w-[26rem] rounded-full bg-honey-soft/50 blur-3xl pointer-events-none" />

        <div className="container mx-auto relative">
          <div className="text-sm text-muted-foreground flex items-center gap-2">
            <Link to="/" className="hover:text-primary">Home</Link>
            <ChevronRight className="h-3.5 w-3.5" />
            <span className="text-primary font-medium">Wholesale</span>
          </div>

          <div className="mt-8 grid lg:grid-cols-12 gap-10 items-end">
            <div className="lg:col-span-7">
              <div className="font-script text-2xl text-leaf">for foodservice operators</div>
              <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl text-primary font-medium leading-[1.02] mt-2 text-balance">
                Wholesale, made<br/>
                <span className="italic font-light text-leaf">plant-simple.</span>
              </h1>
              <p className="mt-6 text-base md:text-lg text-muted-foreground max-w-2xl text-pretty">
                One trusted food service partner. Eight signature plant-based lines. Delivered frozen-to-oven
                or thaw-and-serve — engineered for the operational reality of university dining,
                hotels, YMCAs and high-volume cafés.
              </p>
              <div className="mt-8 flex flex-wrap gap-3">
                <Button asChild size="lg" className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90 px-7 h-12">
                  <a href="#request">Request the wholesale book</a>
                </Button>
                <Button asChild variant="outline" size="lg" className="rounded-full border-primary/25 text-primary hover:bg-secondary px-6 h-12">
                  <Link to="/products">Browse the product line</Link>
                </Button>
              </div>
            </div>
            <div className="lg:col-span-5">
              <div className="rounded-[2rem] overflow-hidden shadow-pop aspect-[4/5]">
                <img src={CAMPUS_IMG} alt="Vegan Danish bakery" className="w-full h-full object-cover" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* WHY VDBF */}
      <section className="py-16 md:py-24 bg-parchment/40">
        <div className="container mx-auto">
          <div className="max-w-2xl">
            <div className="font-script text-2xl text-leaf">why operators pick us</div>
            <h2 className="font-display text-4xl sm:text-5xl text-primary font-medium leading-tight mt-1">
              Pastry built for the back-of-house,<br/>
              <span className="italic font-light text-leaf">loved at the counter.</span>
            </h2>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 mt-12">
            {VALUE_PROPS.map(({ Icon, title, body }, i) => (
              <motion.div
                key={title}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.05 }}
                className="relative rounded-3xl bg-card border border-border/70 p-7 shadow-soft hover:shadow-card hover:-translate-y-1 transition-all duration-500"
              >
                <div className="h-12 w-12 rounded-2xl gradient-leaf grid place-items-center text-primary-foreground shadow-card">
                  <Icon className="h-5 w-5" />
                </div>
                <h3 className="font-display text-xl font-semibold text-primary mt-5">{title}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed mt-2">{body}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* PROGRAM SNAPSHOT */}
      <section className="py-16 md:py-24">
        <div className="container mx-auto grid lg:grid-cols-12 gap-10 items-start">
          <div className="lg:col-span-5">
            <div className="font-script text-2xl text-leaf">program snapshot</div>
            <h2 className="font-display text-3xl sm:text-4xl text-primary font-medium leading-tight mt-1">
              The essentials,<br/><span className="italic font-light text-leaf">in one page.</span>
            </h2>
            <p className="text-muted-foreground mt-5 max-w-md">
              A quick overview of how our wholesale program operates. Full pricing tiers,
              certifications, allergen and macro spec sheets ship with your quote.
            </p>

            <div className="mt-8 inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-secondary text-xs font-medium text-primary">
              <Clock3 className="h-3.5 w-3.5 text-leaf" /> Avg. service time: 42s from freezer to student's hand
            </div>
          </div>
          <div className="lg:col-span-7">
            <div className="grid sm:grid-cols-2 gap-3">
              {PROGRAM.map((p) => (
                <div key={p.l} className="p-5 rounded-2xl bg-card border border-border/70">
                  <div className="text-[11px] uppercase tracking-[0.16em] text-muted-foreground">{p.l}</div>
                  <div className="font-display text-base text-primary font-medium mt-1 leading-snug">{p.v}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* WHO IT'S FOR */}
      <section className="py-16 md:py-24 bg-parchment/40">
        <div className="container mx-auto">
          <div className="max-w-2xl">
            <div className="font-script text-2xl text-leaf">who it's for</div>
            <h2 className="font-display text-3xl sm:text-4xl text-primary font-medium leading-tight mt-1">
              Built for eight kinds of<br/><span className="italic font-light text-leaf">foodservice operator.</span>
            </h2>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-10">
            {ORG_TYPES.map((t) => (
              <div key={t} className="flex items-center gap-3 p-5 rounded-2xl bg-card border border-border/70 hover:shadow-card transition-shadow">
                <div className="h-10 w-10 shrink-0 rounded-xl bg-secondary text-leaf grid place-items-center">
                  <Handshake className="h-4 w-4" />
                </div>
                <div className="font-display text-base text-primary font-medium leading-snug">{t}</div>
              </div>
            ))}
          </div>

          <div className="mt-10 flex items-center justify-center gap-2 text-xs uppercase tracking-[0.18em] text-muted-foreground">
            <Leaf className="h-3.5 w-3.5 text-leaf" /> One partner · every daypart · plant-based by default
          </div>
        </div>
      </section>

      {/* REQUEST FORM */}
      <div id="request">
        <Wholesale />
      </div>

      <Footer />
    </main>
  );
}
